import React, { useState, useEffect } from 'react';
import { useAPR } from '../context/APRContext';
import { useAuth } from '../context/AuthContext';
import { APRRun, APRRunStatus, APRModel } from '../types';
import apiService from '../services/api';

const Models: React.FC = () => {
  const { state, dispatch } = useAPR();
  const { hasPermission } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [configuringRun, setConfiguringRun] = useState<APRRun | null>(null);
  const [viewingRun, setViewingRun] = useState<APRRun | null>(null);
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [editingRun, setEditingRun] = useState<APRRun | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<APRRunStatus | 'All'>('All');
  const [formData, setFormData] = useState({
    rationalizationName: '',
    description: '',
    status: 'Created' as APRRunStatus
  });

  // Function to load backend models and create APR Runs from them
  const loadBackendModels = async () => {
    try {
      // Fetch all models from backend
      const backendModels = await apiService.getModels();
      console.log('📥 Loaded', backendModels.length, 'models from backend');
      
      // Create APR Runs from backend models
      const runsFromBackend: APRRun[] = backendModels.map((model: APRModel) => ({
        id: model.id,
        rationalizationName: model.name,
        description: model.description || '',
        status: model.status as APRRunStatus,
        applicationCount: model.applicationCount || 0,
        createdDate: new Date().toISOString(),
        selectedApplicationIds: [] // Will be loaded when needed
      }));
      
      // Set all runs at once
      dispatch({ type: 'SET_APR_RUNS', payload: runsFromBackend });
      console.log('✅ Loaded/refreshed', runsFromBackend.length, 'APR runs from backend models');
    } catch (error) {
      console.error('Failed to load backend models:', error);
    }
  };

  // Load backend models on mount
  useEffect(() => {
    loadBackendModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only
  
  const filteredRuns = state.aprRuns.filter(run => 
    selectedStatus === 'All' || run.status === selectedStatus
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingRun) {
        // Update existing APR model in database
        console.log('📝 Updating existing APR model in database');
        await apiService.updateModel(editingRun.id, {
          name: formData.rationalizationName,
          description: formData.description,
          status: formData.status,
          applicationCount: editingRun.applicationCount || 0
        });
        console.log('✅ APR model updated in database');
      } else {
        // Create new APR model in database
        console.log('📝 Creating new APR model in database');
        await apiService.createModel({
          name: formData.rationalizationName,
          description: formData.description,
          status: 'Created', // Always set to Created for new APR models
          applicationCount: 0
        });
        console.log('✅ APR model created in database');
      }
      
      // Reload ALL data from backend to ensure consistency
      console.log('🔄 Reloading all models from backend...');
      await loadBackendModels();
      
      resetForm();
    } catch (error) {
      console.error('Failed to save APR model:', error);
      alert('Failed to save APR model. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      rationalizationName: '',
      description: '',
      status: 'Created'
    });
    setShowForm(false);
    setEditingRun(null);
  };

  const handleEdit = (run: APRRun) => {
    if (run.status === 'In-Process') {
      alert('APR models with "In-Process" status cannot be edited as users are currently working on the APR.');
      return;
    }
    setEditingRun(run);
    setFormData({
      rationalizationName: run.rationalizationName,
      description: run.description,
      status: run.status
    });
    setShowForm(true);
  };

  const handleSchedule = async (run: APRRun) => {
    if (run.status !== 'Created') {
      alert('Only APR models with "Created" status can be scheduled');
      return;
    }

    if (run.applicationCount === 0) {
      alert('Please configure applications first before scheduling this APR model');
      return;
    }

    if (window.confirm(`Are you sure you want to schedule "${run.rationalizationName}"? This will change its status to Scheduled.`)) {
      try {
        // Update backend model status
        console.log('📝 Updating backend model status to Scheduled');
        await apiService.updateModel(run.id, {
          name: run.rationalizationName,
          description: run.description,
          status: 'Scheduled',
          applicationCount: run.applicationCount
        });
        
        // Reload ALL data from backend to ensure consistency
        console.log('🔄 Reloading all models from backend...');
        await loadBackendModels();
        console.log('✅ Model scheduled successfully');
      } catch (error) {
        console.error('Failed to schedule model:', error);
        alert('Failed to schedule model. Please try again.');
      }
    }
  };

  const handleUnschedule = async (run: APRRun) => {
    if (run.status !== 'Scheduled') {
      alert('Only APR models with "Scheduled" status can be unscheduled');
      return;
    }

    if (window.confirm(`Are you sure you want to unschedule "${run.rationalizationName}"? This will:\n- Change the status back to "Created"\n- Remove it from APR Execution queue\n- Keep all assigned applications (${run.applicationCount || 0} apps)`)) {
      try {
        // Update backend model status
        console.log('📝 Updating backend model status to Created');
        await apiService.updateModel(run.id, {
          name: run.rationalizationName,
          description: run.description,
          status: 'Created',
          applicationCount: run.applicationCount
        });
        
        // Reload ALL data from backend to ensure consistency
        console.log('🔄 Reloading all models from backend...');
        await loadBackendModels();
        console.log('✅ Model unscheduled successfully, applications preserved');
      } catch (error) {
        console.error('Failed to unschedule model:', error);
        alert('Failed to unschedule model. Please try again.');
      }
    }
  };

  const handleViewApplications = (run: APRRun) => {
    setViewingRun(run);
    setShowViewModal(true);
  };

  const handleModelDelete = (run: APRRun) => {
    if (!hasPermission('canManageUsers')) {
      alert('Access denied. Only Administrators can delete APR models.');
      return;
    }

    // Create status-specific warning messages
    let statusWarning = '';
    if (run.status === 'In-Process') {
      statusWarning = `\n⚠️  CRITICAL WARNING: This APR model is currently "In-Process" - users may be actively working on application scoring and their work will be LOST!\n`;
    } else if (run.status === 'Scheduled') {
      statusWarning = `\n⚠️  WARNING: This APR model is scheduled for execution and will be removed from the execution queue.\n`;
    } else if (run.status === 'Completed') {
      statusWarning = `\n⚠️  WARNING: This APR model is completed and contains valuable analysis results that will be permanently lost.\n`;
    }

    const confirmMessage = `Are you sure you want to PERMANENTLY DELETE the APR model "${run.rationalizationName}"?${statusWarning}\nThis action cannot be undone and will:\n- Remove the model completely\n- Delete all associated data and user work\n- Cannot be recovered\n\nAs an Administrator, you can delete APR models at any status level.\n\nType "DELETE" to confirm this permanent action.`;
    
    const userConfirmation = prompt(confirmMessage);
    
    if (userConfirmation === 'DELETE') {
      dispatch({ type: 'DELETE_APR_RUN', payload: run.id });
      alert(`✅ APR model "${run.rationalizationName}" (${run.status}) has been permanently deleted by Administrator.`);
    } else if (userConfirmation !== null) {
      alert('❌ Deletion cancelled. You must type "DELETE" exactly to confirm.');
    }
  };

  const getSelectedApplicationsForRun = (run: APRRun) => {
    if (!run.selectedApplicationIds || run.selectedApplicationIds.length === 0) {
      return [];
    }
    return state.applications.filter(app => 
      run.selectedApplicationIds!.includes(app.id)
    );
  };

  const getTotalUsersForRuns = (runs: APRRun[]) => {
    return runs.reduce((totalUsers, run) => {
      const runApplications = getSelectedApplicationsForRun(run);
      const runUsers = runApplications.reduce((sum, app) => sum + (app.userCount || 0), 0);
      return totalUsers + runUsers;
    }, 0);
  };

  const handleConfigure = async (run: APRRun) => {
    if (run.status !== 'Created') {
      alert('Configuration is only allowed for APR models with "Created" status. Models in Scheduled, In-Process, or Completed status cannot be reconfigured.');
      return;
    }
    
    setConfiguringRun(run);
    setLoadingApplications(true);
    setSelectedApplications([]); // Start with empty while loading
    
    // Show modal immediately
    setShowConfigureModal(true);
    
    // ALWAYS load applications from backend for consistency
    try {
      console.log('📥 Loading applications from backend for model:', run.id);
      const backendApplications = await apiService.getModelApplications(run.id);
      const backendAppIds = backendApplications.map((app: any) => app.id);
      console.log('📥 Loaded', backendAppIds.length, 'applications from backend');
      
      setSelectedApplications(backendAppIds);
      
      // Update the run with the loaded application IDs
      if (backendAppIds.length > 0) {
        const updatedRun: APRRun = {
          ...run,
          selectedApplicationIds: backendAppIds,
          applicationCount: backendAppIds.length
        };
        dispatch({ type: 'UPDATE_APR_RUN', payload: updatedRun });
      }
    } catch (error) {
      console.error('Failed to load applications from backend:', error);
      // Fall back to empty selection if backend fails
      setSelectedApplications([]);
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleConfigureSubmit = async () => {
    if (!configuringRun) return;
    
    try {
      // ALWAYS save to backend
      console.log('💾 Saving', selectedApplications.length, 'applications to backend for model:', configuringRun.id);
      await apiService.updateModelApplications(configuringRun.id, selectedApplications);
      
      // Update local state
      const updatedRun: APRRun = {
        ...configuringRun,
        selectedApplicationIds: selectedApplications,
        applicationCount: selectedApplications.length
      };
      
      dispatch({ type: 'UPDATE_APR_RUN', payload: updatedRun });
      
      // Also update the backend model's application count
      await apiService.updateModel(configuringRun.id, {
        name: configuringRun.rationalizationName,
        description: configuringRun.description,
        status: configuringRun.status,
        applicationCount: selectedApplications.length
      });
      
      closeConfigureModal();
      
      // Reload ALL data from backend to ensure consistency
      console.log('🔄 Reloading all models from backend...');
      await loadBackendModels();
      console.log('✅ Configuration saved successfully');
    } catch (error) {
      console.error('Failed to save configuration:', error);
      alert('Failed to save application configuration. Please try again.');
    }
  };

  const getAvailableApplications = () => {
    // Get all applications that are not assigned to any APR run except the current one being configured
    const assignedAppIds = state.aprRuns
      .filter(run => run.id !== configuringRun?.id)
      .flatMap(run => run.selectedApplicationIds || []);
    
    return state.applications.filter(app => !assignedAppIds.includes(app.id));
  };

  const handleApplicationToggle = (appId: string) => {
    setSelectedApplications(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const closeConfigureModal = () => {
    setShowConfigureModal(false);
    setConfiguringRun(null);
    setSelectedApplications([]);
    setLoadingApplications(false);
  };

  const handleDelete = async (run: APRRun) => {
    // Check if run is in execution (Scheduled, In-Process or Completed)
    if (run.status === 'Scheduled' || run.status === 'In-Process' || run.status === 'Completed') {
      alert('Model is scheduled or in APR execution and thus cannot be deleted');
      return;
    }
    
    // For Created status, require password
    const password = prompt('Enter password to delete this APR model:');
    if (password !== 'ABC123') {
      alert('Incorrect password. Deletion cancelled.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete "${run.rationalizationName}"?`)) {
      try {
        // Delete from database
        console.log('🗑️ Deleting APR model from database');
        await apiService.deleteModel(run.id);
        console.log('✅ APR model deleted from database');
        
        // Reload ALL data from backend to ensure consistency
        console.log('🔄 Reloading all models from backend...');
        await loadBackendModels();
      } catch (error) {
        console.error('Failed to delete APR model:', error);
        alert('Failed to delete APR model. Please try again.');
      }
    }
  };

  const getStatusChip = (status: APRRunStatus) => {
    const chipStyles = {
      'Created': 'status-chip status-chip--created',
      'Scheduled': 'status-chip status-chip--scheduled',
      'In-Process': 'status-chip status-chip--in-process',
      'Completed': 'status-chip status-chip--completed'
    }[status];
    
    const icons = {
      'Created': '🆕',
      'Scheduled': '📅',
      'In-Process': '⏳',
      'Completed': '✅'
    }[status];
    
    return (
      <span className={chipStyles}>
        <span className="status-chip__icon">{icons}</span>
        <span className="status-chip__text">{status}</span>
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="md-page">
      {/* Material Design App Bar */}
      <div className="md-app-bar">
        <div className="md-app-bar__content">
          <div className="md-app-bar__title-section">
            <h1 className="md-app-bar__title">APR Models</h1>
            <p className="md-app-bar__subtitle">
              Manage Application Portfolio Rationalization runs and track their progress
            </p>
          </div>
          <div className="md-app-bar__actions">
            {/* Filter Chip */}
            <div className="md-filter-section">
              <label htmlFor="statusFilter" className="md-filter-label">Filter by status</label>
              <select
                id="statusFilter"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as APRRunStatus | 'All')}
                className="md-select"
              >
                <option value="All">All Statuses</option>
                <option value="Created">Created</option>
                <option value="Scheduled">Scheduled</option>
                <option value="In-Process">In-Process</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            {/* FAB */}
            <button 
              className="md-fab md-fab--primary"
              onClick={() => setShowForm(true)}
              aria-label="Create new APR run"
            >
              <span className="md-fab__icon">+</span>
              <span className="md-fab__label">New APR Run</span>
            </button>
          </div>
        </div>
      </div>

      {/* Material Design Modal */}
      {showForm && (
        <div className="md-scrim" onClick={(e) => e.target === e.currentTarget && resetForm()}>
          <div className="md-dialog">
            <div className="md-dialog__header">
              <h2 className="md-dialog__title">
                {editingRun ? 'Edit APR Run' : 'Create New APR Run'}
              </h2>
              <button 
                className="md-icon-button"
                onClick={resetForm}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="md-dialog__content">
              {/* Text Field - Rationalization Name */}
              <div className="md-text-field">
                <input
                  type="text"
                  id="rationalizationName"
                  name="rationalizationName"
                  value={formData.rationalizationName}
                  onChange={handleInputChange}
                  required
                  className="md-text-field__input"
                  placeholder=" "
                />
                <label htmlFor="rationalizationName" className="md-text-field__label">
                  Rationalization Name *
                </label>
                <div className="md-text-field__outline">
                  <div className="md-text-field__leading"></div>
                  <div className="md-text-field__notch">
                    <div className="md-text-field__label-text">Rationalization Name *</div>
                  </div>
                  <div className="md-text-field__trailing"></div>
                </div>
                <div className="md-text-field__supporting-text">
                  e.g., Q4 2024 Enterprise APR, Legacy Systems Assessment
                </div>
              </div>

              {/* Select Field - Status (Only shown when editing) */}
              {editingRun && (
                <div className="md-select-field">
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                    className="md-select-field__input"
                  >
                    <option value="Created">Created</option>
                    <option value="Scheduled">Scheduled</option>
                    <option value="In-Process">In-Process</option>
                    <option value="Completed">Completed</option>
                  </select>
                  <label htmlFor="status" className="md-select-field__label">
                    Status *
                  </label>
                  <div className="md-select-field__outline">
                    <div className="md-select-field__leading"></div>
                    <div className="md-select-field__notch">
                      <div className="md-select-field__label-text">Status *</div>
                    </div>
                    <div className="md-select-field__trailing">▼</div>
                  </div>
                </div>
              )}

              {/* Status Info for New APR Models */}
              {!editingRun && (
                <div className="md-info-field">
                  <div className="md-info-field__label">Status</div>
                  <div className="md-info-field__value">
                    <span className="status-chip status-chip--created">
                      <span className="status-chip__icon">🆕</span>
                      <span className="status-chip__text">Created</span>
                    </span>
                  </div>
                  <div className="md-info-field__description">
                    New APR models are automatically set to "Created" status
                  </div>
                </div>
              )}

              {/* Text Area - Description */}
              <div className="md-text-field md-text-field--textarea">
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  required
                  className="md-text-field__input"
                  placeholder=" "
                />
                <label htmlFor="description" className="md-text-field__label">
                  Description *
                </label>
                <div className="md-text-field__outline">
                  <div className="md-text-field__leading"></div>
                  <div className="md-text-field__notch">
                    <div className="md-text-field__label-text">Description *</div>
                  </div>
                  <div className="md-text-field__trailing"></div>
                </div>
                <div className="md-text-field__supporting-text">
                  Describe the scope and objectives of this APR run
                </div>
              </div>
            </form>

            <div className="md-dialog__actions">
              <button type="button" className="md-button md-button--text" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" form="apr-form" className="md-button md-button--filled" onClick={handleSubmit}>
                {editingRun ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configure Applications Modal */}
      {showConfigureModal && configuringRun && (
        <div className="md-scrim" onClick={(e) => e.target === e.currentTarget && closeConfigureModal()}>
          <div className="md-dialog" style={{ maxWidth: '800px' }}>
            <div className="md-dialog__header">
              <h2 className="md-dialog__title">
                Configure Applications for "{configuringRun.rationalizationName}"
              </h2>
              <button 
                className="md-icon-button"
                onClick={() => closeConfigureModal()}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>
            
            <div className="md-dialog__content" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <p className="md-dialog__description">
                Select applications from the Application Master Data to include in this APR model scope.
                Each application can only be assigned to one APR model at a time.
              </p>
              
              {/* Selected Applications Section - Always show, with loading state */}
              <div className="md-application-section">
                <h3 className="md-section-title">
                  📋 Selected Applications {loadingApplications ? '(Loading...)' : `(${selectedApplications.length})`}
                </h3>
                {loadingApplications ? (
                  <div className="md-application-list md-selected-apps" style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ color: '#666' }}>Loading applications from database...</div>
                  </div>
                ) : selectedApplications.length > 0 ? (
                  <div className="md-application-list md-selected-apps">
                    {state.applications
                      .filter(app => selectedApplications.includes(app.id))
                      .map(app => (
                        <div key={app.id} className="md-application-item md-selected-item">
                          <label className="md-checkbox">
                            <input
                              type="checkbox"
                              checked={true}
                              onChange={() => handleApplicationToggle(app.id)}
                              className="md-checkbox__input"
                            />
                            <div className="md-checkbox__background">
                              <svg className="md-checkbox__checkmark" viewBox="0 0 24 24">
                                <path className="md-checkbox__checkmark-path" fill="none" stroke="white" d="m1.73,12.91 8.1,8.1 11.5-11.5"/>
                              </svg>
                            </div>
                            <div className="md-application-info">
                              <div className="md-application-name">{app.name}</div>
                              {app.description && (
                                <div className="md-application-description">{app.description}</div>
                              )}
                              {app.applicationIdCode && (
                                <div className="md-application-id">ID: {app.applicationIdCode}</div>
                              )}
                            </div>
                          </label>
                          <span className="md-selected-badge">✅ Selected</span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="md-application-list md-selected-apps" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    No applications currently selected for this APR model.
                  </div>
                )}
              </div>
              
              {/* Available Applications Section */}
              <div className="md-application-section">
                <h3 className="md-section-title">
                  📱 Available Applications ({getAvailableApplications().filter(app => !selectedApplications.includes(app.id)).length})
                </h3>
                <div className="md-application-selection">
                  {getAvailableApplications().filter(app => !selectedApplications.includes(app.id)).length === 0 ? (
                    <div className="md-empty-state">
                      <div className="md-empty-state__icon">📱</div>
                      <h3 className="md-empty-state__title">No Available Applications</h3>
                      <p className="md-empty-state__description">
                        All applications are either already selected or assigned to other APR models.
                      </p>
                    </div>
                  ) : (
                    <div className="md-application-list">
                      {getAvailableApplications()
                        .filter(app => !selectedApplications.includes(app.id))
                        .map(app => (
                          <div key={app.id} className="md-application-item">
                            <label className="md-checkbox">
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={() => handleApplicationToggle(app.id)}
                                className="md-checkbox__input"
                              />
                              <div className="md-checkbox__background">
                                <svg className="md-checkbox__checkmark" viewBox="0 0 24 24">
                                  <path className="md-checkbox__checkmark-path" fill="none" stroke="white" d="m1.73,12.91 8.1,8.1 11.5-11.5"/>
                                </svg>
                              </div>
                              <div className="md-application-info">
                                <div className="md-application-name">{app.name}</div>
                                {app.description && (
                                  <div className="md-application-description">{app.description}</div>
                                )}
                                {app.applicationIdCode && (
                                  <div className="md-application-id">ID: {app.applicationIdCode}</div>
                                )}
                              </div>
                            </label>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="md-dialog__actions">
              <button 
                type="button" 
                className="md-button md-button--text" 
                onClick={() => closeConfigureModal()}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="md-button md-button--filled" 
                onClick={handleConfigureSubmit}
              >
                Save Configuration ({selectedApplications.length} apps selected)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Applications Modal */}
      {showViewModal && viewingRun && (
        <div className="md-scrim" onClick={(e) => e.target === e.currentTarget && setShowViewModal(false)}>
          <div className="md-dialog" style={{ maxWidth: '800px' }}>
            <div className="md-dialog__header">
              <h2 className="md-dialog__title">
                Applications in "{viewingRun.rationalizationName}"
              </h2>
              <button 
                className="md-icon-button"
                onClick={() => setShowViewModal(false)}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>
            
            <div className="md-dialog__content" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {getSelectedApplicationsForRun(viewingRun).length === 0 ? (
                <div className="md-empty-state">
                  <div className="md-empty-state__icon">📱</div>
                  <h3 className="md-empty-state__title">No Applications Configured</h3>
                  <p className="md-empty-state__description">
                    This APR model has no applications configured yet. Use the Configure button to select applications.
                  </p>
                </div>
              ) : (
                <div className="md-application-list">
                  {getSelectedApplicationsForRun(viewingRun).map(app => (
                    <div key={app.id} className="md-application-view-item">
                      <div className="md-application-info">
                        <div className="md-application-name">{app.name}</div>
                        {app.description && (
                          <div className="md-application-description">{app.description}</div>
                        )}
                        <div className="md-application-details">
                          {app.applicationIdCode && (
                            <div className="md-application-detail">
                              <span className="md-application-detail-label">ID:</span>
                              <span className="md-application-detail-value">{app.applicationIdCode}</span>
                            </div>
                          )}
                          {app.technologyStack && (
                            <div className="md-application-detail">
                              <span className="md-application-detail-label">Technology:</span>
                              <span className="md-application-detail-value">{app.technologyStack}</span>
                            </div>
                          )}
                          {app.businessFunctionDomain && (
                            <div className="md-application-detail">
                              <span className="md-application-detail-label">Business Domain:</span>
                              <span className="md-application-detail-value">{app.businessFunctionDomain}</span>
                            </div>
                          )}
                          {app.userCount && (
                            <div className="md-application-detail">
                              <span className="md-application-detail-label">Users:</span>
                              <span className="md-application-detail-value">{app.userCount.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="md-dialog__actions">
              <button 
                type="button" 
                className="md-button md-button--filled" 
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Material Design Cards Layout */}
      <div className="md-content">
        {/* Statistics Cards */}
        <div className="md-stats-grid">
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">{filteredRuns.length}</div>
                <div className="md-stat__label">Total APR Runs</div>
              </div>
            </div>
          </div>
          
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">
                  {getTotalUsersForRuns(filteredRuns).toLocaleString()}
                </div>
                <div className="md-stat__label">Users</div>
              </div>
            </div>
          </div>
          
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">
                  {filteredRuns.reduce((sum, run) => sum + run.applicationCount, 0)}
                </div>
                <div className="md-stat__label">Applications in Scope</div>
              </div>
            </div>
          </div>
          
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">
                  {filteredRuns.filter(run => run.status === 'Completed').length}
                </div>
                <div className="md-stat__label">Completed</div>
              </div>
            </div>
          </div>
          
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">
                  {filteredRuns.filter(run => run.status === 'In-Process').length}
                </div>
                <div className="md-stat__label">In-Process</div>
              </div>
            </div>
          </div>
        </div>

        {/* APR Runs Display */}
        {filteredRuns.length === 0 ? (
          <div className="md-empty-state">
            <div className="md-empty-state__icon">🎯</div>
            <h3 className="md-empty-state__title">No APR Runs</h3>
            <p className="md-empty-state__description">
              {selectedStatus === 'All' 
                ? 'Create your first APR run to start rationalizing applications.' 
                : `No APR runs with status "${selectedStatus}".`
              }
            </p>
            <button 
              className="md-button md-button--filled"
              onClick={() => setShowForm(true)}
            >
              Create First APR Run
            </button>
          </div>
        ) : (
          <div className="md-data-grid">
            {filteredRuns.map(run => (
              <div key={run.id} className="md-card md-card--outlined md-run-card">
                <div className="md-card__content">
                  <div className="md-run-card__header">
                    <div className="md-run-card__title-section">
                      <h3 className="md-run-card__title">{run.rationalizationName}</h3>
                      <div className="md-run-card__meta">
                        <span className="md-run-card__app-count">
                          <span className="md-run-card__app-count-value">{run.applicationCount}</span>
                          <span className="md-run-card__app-count-label"> applications</span>
                        </span>
                      </div>
                    </div>
                    <div className="md-run-card__status">
                      {getStatusChip(run.status)}
                    </div>
                  </div>
                  
                  <div className="md-run-card__description">
                    <p>{run.description}</p>
                  </div>
                  
                  <div className="md-run-card__dates">
                    <div className="md-date-info">
                      <span className="md-date-info__label">Created</span>
                      <span className="md-date-info__value">{formatDate(run.createdDate)}</span>
                    </div>
                    {run.completedDate && (
                      <div className="md-date-info">
                        <span className="md-date-info__label">Completed</span>
                        <span className="md-date-info__value">{formatDate(run.completedDate)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="md-card__actions md-card__actions--split">
                  <div className="md-card__actions-left">
                    <button 
                      className="md-button md-button--text"
                      onClick={() => handleConfigure(run)}
                      disabled={run.status !== 'Created'}
                    >
                      Configure
                    </button>
                    <button 
                      className="md-button md-button--text"
                      onClick={() => handleViewApplications(run)}
                      disabled={!run.selectedApplicationIds || run.selectedApplicationIds.length === 0}
                    >
                      View Apps ({run.selectedApplicationIds?.length || 0})
                    </button>
                    <button 
                      className="md-button md-button--text"
                      onClick={() => handleEdit(run)}
                      disabled={run.status === 'In-Process'}
                    >
                      Edit
                    </button>
                    {run.status === 'Scheduled' && (
                      <button 
                        className="md-button md-button--text md-button--warning"
                        onClick={() => handleUnschedule(run)}
                      >
                        Unschedule
                      </button>
                    )}
                    <button 
                      className="md-button md-button--text md-button--error"
                      onClick={() => handleDelete(run)}
                    >
                      Delete
                    </button>
                    {hasPermission('canManageUsers') && (
                      <button 
                        className="md-button md-button--text md-button--error"
                        onClick={() => handleModelDelete(run)}
                        title="Administrator only - Permanent model deletion"
                      >
                        Model Delete
                      </button>
                    )}
                  </div>
                  <div className="md-card__actions-right">
                    <button 
                      className="md-button md-button--filled"
                      onClick={() => handleSchedule(run)}
                      disabled={run.status !== 'Created'}
                    >
                      Schedule
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Models;